import { Link } from "react-router-dom";
import { ArrowRight, Zap, TrendingUp, Wallet, Bot } from "lucide-react";
import InstallPWA from "@/components/InstallPWA";
import { useAuth } from "@/context/AuthContext";

const heroImg = "/img/hero-cover.png";

export default function Landing() {
  const { user } = useAuth();
  const isSubscriber = !!user?.subscription_active;
  return (
    <div data-testid="landing-page" className="relative overflow-hidden">
      {/* HERO */}
      <section className="relative grid-bg noise-bg">
        <div className="max-w-7xl mx-auto px-4 md:px-8 pt-16 pb-24 grid md:grid-cols-12 gap-10 items-center relative z-10">
          <div className="md:col-span-7">
            {!isSubscriber && <span className="sticker" data-testid="hero-sticker">NOVO • R$ 15,99/MÊS</span>}
            {isSubscriber && <span className="sticker" style={{background:'#CCFF00', color:'#050505'}} data-testid="hero-sticker-active">ASSINATURA ATIVA ✓</span>}
            <h1 className="font-bebas text-6xl md:text-8xl lg:text-9xl leading-[0.85] mt-6 tracking-tighter">
              FINANÇAS<br/>
              <span className="text-[#CCFF00]">DO SEU JEITO.</span><br/>
              SEM ENROLAÇÃO.
            </h1>
            <p className="mt-8 max-w-xl text-lg md:text-xl text-white/70 font-manrope">
              Aprenda a guardar, investir e sair de dívida sem o discurso de tiozão de banco.
              Curso rápido, missões diárias, radar de gasto e um coach com IA no seu bolso.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              {isSubscriber ? (
                <>
                  <Link to="/dashboard" data-testid="hero-cta-dashboard" className="brutal-btn">
                    Ir pro Dashboard <ArrowRight className="ml-2" size={20}/>
                  </Link>
                  <Link to="/cursos" data-testid="hero-cta-cursos" className="brutal-btn brutal-btn-outline">
                    Ver os cursos
                  </Link>
                </>
              ) : (
                <>
                  <Link to="/register" data-testid="hero-cta-register" className="brutal-btn">
                    Começar agora <ArrowRight className="ml-2" size={20}/>
                  </Link>
                  <Link to="/cursos" data-testid="hero-cta-cursos" className="brutal-btn brutal-btn-outline">
                    Ver os cursos
                  </Link>
                </>
              )}
              <InstallPWA variant="hero"/>
            </div>
            <div className="mt-8 flex items-center gap-6 text-sm text-white/50 font-manrope uppercase tracking-widest">
              <span>+12 cursos</span>
              <span className="w-1 h-1 bg-white/30 rounded-full"/>
              <span>Coach AI 24/7</span>
              <span className="w-1 h-1 bg-white/30 rounded-full"/>
              <span>Cancela quando quiser</span>
            </div>
          </div>

          <div className="md:col-span-5 relative">
            <div className="relative">
              <img src={heroImg} alt="Jovem brasileiro de hoodie em viela urbana" className="w-full h-[520px] object-cover object-top border border-white/[0.08]" style={{filter:'contrast(1.08) saturate(0.95) brightness(0.95)'}}/>
              {!isSubscriber && (
                <>
                  <div className="absolute top-5 left-5 inline-flex items-center gap-2 bg-black/60 backdrop-blur-md border border-white/15 px-3 py-1.5 font-manrope text-[10px] uppercase tracking-[0.2em] text-white/80">
                    <span className="w-1.5 h-1.5 bg-[#D4FF00] rounded-full animate-pulse"/> DINHEIRO NA MÃO
                  </div>
                  <div className="absolute bottom-5 right-5 bg-[#D4FF00] text-[#070707] font-manrope font-black text-base px-4 py-2 tracking-tight">
                    R$ 15,99<span className="font-medium text-xs opacity-70">/mês</span>
                  </div>
                </>
              )}
              {isSubscriber && (
                <div className="absolute bottom-5 right-5 bg-[#D4FF00] text-[#070707] font-manrope font-black text-sm px-4 py-2 tracking-widest uppercase">
                  Você tá dentro
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="border-y border-white/[0.06] bg-[#0A0A0A] relative">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-20 grid md:grid-cols-4 gap-6">
          {[
            {icon: <Zap size={32}/>, title: "Missões diárias", desc: "Ganha XP fazendo pequenas ações. Vira hábito."},
            {icon: <Wallet size={32}/>, title: "Radar AI", desc: "A IA aponta onde tua grana está vazando."},
            {icon: <TrendingUp size={32}/>, title: "Simulador ao vivo", desc: "Vê teus juros compostos em tempo real."},
            {icon: <Bot size={32}/>, title: "Coach AI 24/7", desc: "Tira dúvida a hora que quiser."},
          ].map((f, i) => (
            <div key={i} data-testid={`feature-${i}`} className="brutal-card">
              <div className="text-[#CCFF00] mb-4">{f.icon}</div>
              <h3 className="font-bebas text-2xl">{f.title}</h3>
              <p className="text-white/60 mt-2 font-manrope text-sm">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* PRICING — hidden for active subscribers */}
      {!isSubscriber && (
        <section className="relative">
          <div className="max-w-4xl mx-auto px-4 md:px-8 py-24 text-center">
            <span className="sticker">O PLANO</span>
            <h2 className="font-bebas text-6xl md:text-7xl mt-6 tracking-tighter">UM PLANO. TODO O CONTEÚDO.</h2>
            <div className="mt-12 brutal-card brutal-shadow max-w-md mx-auto text-left" style={{borderColor:'#CCFF00'}}>
              <div className="font-bebas text-[#CCFF00] text-lg tracking-widest">ASSINATURA CRIA</div>
              <div className="mt-4 flex items-end gap-2">
                <span className="font-bebas text-6xl text-white">R$ 15,99</span>
                <span className="text-white/50 font-manrope pb-2">/mês</span>
              </div>
              <ul className="mt-6 space-y-3 font-manrope text-white/80">
                <li>✓ Todos os cursos liberados</li>
                <li>✓ Coach AI ilimitado (Claude Sonnet)</li>
                <li>✓ Radar de Vazamento com IA</li>
                <li>✓ Missões diárias + Simulador</li>
                <li>✓ Cancela quando quiser, sem multa</li>
              </ul>
              <Link to="/register" data-testid="pricing-cta" className="brutal-btn w-full mt-8">
                Assinar agora
              </Link>
              <div className="mt-3 text-center text-white/50 font-manrope text-xs">Pagamento via Pix</div>
            </div>
          </div>
        </section>
      )}

      {/* Active subscriber shortcut */}
      {isSubscriber && (
        <section className="relative">
          <div className="max-w-4xl mx-auto px-4 md:px-8 py-24 text-center">
            <span className="sticker" style={{background:'#CCFF00', color:'#050505'}}>TÁ TUDO LIBERADO</span>
            <h2 className="font-bebas text-6xl md:text-7xl mt-6 tracking-tighter">
              BORA MANTER O <span className="text-[#CCFF00]">CORRE</span>
            </h2>
            <p className="mt-4 text-white/60 font-manrope max-w-xl mx-auto">Missões diárias, XP e cursos te esperando. Continua de onde parou.</p>
            <div className="mt-8 flex flex-wrap gap-4 justify-center">
              <Link to="/missao" data-testid="active-cta-missao" className="brutal-btn">Fazer missões de hoje</Link>
              <Link to="/coach" data-testid="active-cta-coach" className="brutal-btn brutal-btn-outline">Falar com Coach AI</Link>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
